import { Badge } from '@/components/shadcn/badge'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Skeleton } from '@/components/shadcn/skeleton'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { AgentIcon } from '@/const'
import { SectionLayout } from '@/pages/shared-components/SectionLayout'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { Link } from 'react-router'
import { useState } from 'react'
import { AgentStudioBreadcrumbs } from '../AgentStudioBreadcrumbs'

type UserAgentSummary = RestSchemas['Messages.UserAgent.UserAgentSummary']
type UserAgentWithId = UserAgentSummary & { id: number }

export const AgentStudioAgentsPage = () => {
  const { getUserAgentApiGetUserAgents } = useRestClient()
  const { data: agents, isError, isLoading } = getUserAgentApiGetUserAgents.useQuery()
  const [searchString, setSearchString] = useState('')
  const agentsWithId =
    agents?.filter((agent): agent is UserAgentWithId => agent.id !== undefined && agent.id !== null) ?? []
  const searchTerm = searchString.trim().toLowerCase()
  const filteredAgents = agentsWithId.filter((agent) => {
    if (!searchTerm) {
      return true
    }

    const agentName = agent.name ?? agent.config.name ?? agent.config.key
    const description = agent.config.description ?? ''
    return agentName.toLowerCase().includes(searchTerm) || description.toLowerCase().includes(searchTerm)
  })

  return (
    <>
      <AgentStudioBreadcrumbs title="Agents">
        <SearchInput
          value={searchString}
          onChange={setSearchString}
          placeholder="Search by name or description"
          className="w-48 sm:w-72"
        />
      </AgentStudioBreadcrumbs>
      <SectionLayout>
        {isLoading ? (
          <AgentCardsLoading />
        ) : isError ? (
          <ErrorComponent />
        ) : (
          <>
            <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
              {filteredAgents.map((agent) => (
                <AgentCard key={agent.id} agent={agent} />
              ))}
              <NewAgentCard />
            </div>
            {filteredAgents.length === 0 && (
              <NoSearchResultsDisplay text={searchTerm ? 'No agents match your search' : 'No agents found'} />
            )}
          </>
        )}
      </SectionLayout>
    </>
  )
}

function NewAgentCard() {
  return (
    <Link
      to="/agent-studio/agents/new"
      className="group relative flex h-full min-h-56 w-full min-w-0 cursor-pointer appearance-none flex-col items-center justify-center gap-2 rounded-md border-[3px] border-dashed border-border bg-background p-3 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <span className="flex size-28 shrink-0 items-center justify-center" aria-hidden>
        <IconPlusOutline className="size-16 text-border transition-colors group-hover:text-muted-foreground" />
      </span>
      <span className="max-w-full truncate text-center text-sm text-muted-foreground">New agent</span>
    </Link>
  )
}

function AgentCard({ agent }: { agent: UserAgentWithId }) {
  const agentName = agent.name ?? agent.config.name ?? agent.config.key
  const description = agent.config.description || 'No description provided.'
  const skillCount = agent.config.skillKeys?.length ?? 0
  const toolCount = agent.config.tools?.length ?? 0

  return (
    <Link
      className="group block h-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      to={`/agent-studio/agents/${agent.id}`}
    >
      <Card className="h-full pb-0 transition-all duration-300 group-hover:bg-primary/5 group-hover:shadow-md">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="flex size-[3.25rem] shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <AgentIcon className="size-7" />
            </div>
            <div className="min-w-0 space-y-2">
              <CardTitle className="truncate">{agentName}</CardTitle>
              <Badge variant={agent.isPublic ? 'success' : 'secondary'}>{agent.isPublic ? 'Public' : 'Private'}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="line-clamp-3 text-sm text-muted-foreground">{description}</p>
        </CardContent>
        <CardFooter className="mt-auto flex-wrap gap-2">
          {agent.sharedVia?.map((reason) => (
            <Badge key={reason} variant="outline">
              {sharedViaLabel(reason)}
            </Badge>
          ))}
          <Badge className="max-w-full truncate" variant="outline">
            {agent.config.model}
          </Badge>
          <Badge variant="outline">
            {skillCount} {skillCount === 1 ? 'skill' : 'skills'}
          </Badge>
          <Badge variant="outline">
            {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
          </Badge>
        </CardFooter>
      </Card>
    </Link>
  )
}

function sharedViaLabel(reason: string) {
  switch (reason) {
    case 'owner':
      return 'Owner'
    case 'public':
      return 'Public'
    case 'user':
      return 'User'
    case 'organisation':
      return 'Organisation'
    case 'project':
      return 'Project'
    case 'team':
      return 'Team'
    default:
      return reason
  }
}

function AgentCardsLoading() {
  return (
    <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-56 w-full" />
      ))}
    </div>
  )
}
